import { Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

const tiers = [
  {
    id: "gym",
    name: "Gym Licence",
    label: "Annual Unlimited Licence",
    valueHook: "Unlimited Member Audits.",
    tagline: "For boutique studios & gym groups.",
    anchorPrice: "£299",
    price: "£149",
    cadence: "per year · billed via Stripe",
    href: "https://buy.stripe.com/fZudR89vvdpP9WA9EP6AM06",
    features: [
      "Member retention dashboard",
      "Lead reveal logic (opt-in only)",
      "Unique audit links + custom slug + QR codes",
      "7/14/30-day CSV exports",
      "Branded 3-page PDF reports",
      "Automated Re-engagement Triggers",
    ],
    highlighted: false,
    testid: "pricing-gym",
  },
  {
    id: "business",
    name: "Business Licence",
    label: "Annual Unlimited Licence",
    valueHook: "Unlimited Workforce Audits.",
    tagline: "For HR, People & Wellbeing teams.",
    anchorPrice: "£399",
    price: "£249",
    cadence: "per year · billed via Stripe",
    href: "https://buy.stripe.com/8x29ASdLL99z3ycbMX6AM05",
    features: [
      "Workforce performance audit",
      "Anonymised enterprise reporting",
      "Departmental Health Benchmarking",
      "Aggregated dashboards (no PII to managers)",
      "Priority support & onboarding",
      "DPA & SSO available on request",
    ],
    highlighted: true,
    testid: "pricing-business",
  },
];

export default function Pricing() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <span className="eyebrow">Pricing — Annual Flat Fee</span>
        <h1 className="font-display tracking-tighter text-5xl sm:text-6xl mt-4">
          One licence. <span className="shine-text">Unlimited audits.</span>
        </h1>
        <p className="text-slate-300 mt-4">
          Pick the licence that fits your business. One annual fee, unlimited audits, zero per-seat surprises.
        </p>
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto items-stretch">
        {tiers.map((t) => (
          <div
            key={t.id}
            className={`relative rounded-3xl p-8 sm:p-10 flex flex-col ${
              t.highlighted
                ? "border border-[var(--border-active)] bg-gradient-to-br from-[#0D1B2A] to-[#0A0E17]"
                : "glass"
            }`}
            data-testid={t.testid}
          >
            {t.highlighted && (
              <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase bg-[#00BFFF] text-[#0A0E17] font-mono">
                Most popular
              </div>
            )}

            <div className="font-mono text-xs text-slate-500">{t.id.toUpperCase()}</div>
            <h2 className="font-display text-3xl mt-2">{t.name}</h2>
            <div className="mt-1 text-xs uppercase tracking-[0.18em] text-brand font-mono" data-testid={`${t.testid}-label`}>
              {t.label}
            </div>
            <p className="text-slate-300 mt-3 text-sm font-medium" data-testid={`${t.testid}-hook`}>{t.valueHook}</p>
            <p className="text-slate-500 mt-1 text-xs">{t.tagline}</p>

            {/* Price block — anchored */}
            <div className="mt-6 flex items-end gap-3 flex-wrap">
              <span
                className="font-display text-2xl text-slate-500 line-through decoration-2 decoration-slate-600"
                data-testid={`${t.testid}-anchor`}
              >
                {t.anchorPrice}
              </span>
              <span className="font-display text-5xl text-white" data-testid={`${t.testid}-price`}>
                {t.price}
              </span>
              <span className="text-xs text-slate-500 mb-2">{t.cadence}</span>
            </div>

            <a href={t.href} target="_blank" rel="noopener noreferrer" className="block mt-6">
              <Button
                className="w-full h-12 rounded-full btn-glow"
                data-testid={`${t.testid}-cta`}
              >
                Activate {t.name}
              </Button>
            </a>

            <ul className="mt-8 space-y-3 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="text-center mt-14">
        <p className="text-slate-400">
          Need something custom?{" "}
          <Link to="/contact" className="text-brand hover:underline">
            Talk to sales →
          </Link>
        </p>
      </div>
    </div>
  );
}
